import {
  ColumnPinningState,
  createColumnHelper,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Text } from "@chakra-ui/react";

import {
  DocumentType,
  FragmentType,
  getFragmentData,
  gql,
} from "__generated__/gql";
import {
  SkuBrandOrderingPlatformFilter,
  SkusOrderBy,
} from "__generated__/gql/graphql";
import FullPageTable, {
  useFullPageTableProps,
} from "components/Shared/ChakraReactTable/FullPageTable";
import usePagination, {
  usePaginationReset,
} from "components/Shared/ChakraReactTable/usePagination";
import useFixedLayout from "components/Shared/ChakraReactTable/useFixedLayout";
import { usePrepApolloData } from "components/Shared/ChakraReactTable/utils";
import useGQLSorting, {
  GQLOrderByEnumsById,
} from "components/Shared/ChakraReactTable/useGQLSorting";
import { useBrandQuery } from "utils/apollo/hooks/brand";
import { useCurrentBrandId } from "utils/currentUserHooks";
import { assertPresence } from "utils/assertions";
import CustomerPresenceTableSkuCell from "components/Customers/Presence/SkuCell";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { compact, flatten, groupBy, isEqual, keyBy } from "lodash";
import CustomerProductPresenceTableCell from "components/Customers/Presence/TableCell";
import useCombineFilters from "utils/useCombineFilters";
import { TriangleUpIcon } from "@chakra-ui/icons";
import TooltipIconButton from "components/Shared/TooltipIconButton";

const COLUMN_PAGE_SIZE = 10;

const bopFragment = gql(/* GraphQL */ `
  fragment CustomerProductPresenceCustomerColumn on BrandOrderingPlatform {
    id
    orderingPlatform {
      id
      name
    }
  }
`);

const setupQuery = gql(/* GraphQL */ `
  query CustomerPresenceTableSetup(
    $filter: SkuBrandOrderingPlatformFilter!
    $brandOrderingPlatformOrderBy: [BrandOrderingPlatformsOrderBy!] = [
      ORDERING_PLATFORM__NAME_ASC
    ]
    $brandOrderingPlatformLimit: Int
    $brandOrderingPlatformOffset: Int
    $skuOrderBy: [SkusOrderBy!] = [FULL_NAME_ASC]
    $skuLimit: Int
    $skuOffset: Int
  ) {
    brandOrderingPlatforms(
      filter: {
        and: [
          { orderingPlatform: { parentId: { isNull: true } } }
          {
            or: [
              { skuBrandOrderingPlatforms: { some: $filter } }
              {
                orderingPlatform: {
                  childOrderingPlatforms: {
                    some: {
                      brandOrderingPlatforms: {
                        some: { skuBrandOrderingPlatforms: { some: $filter } }
                      }
                    }
                  }
                }
              }
            ]
          }
        ]
      }
      orderBy: $brandOrderingPlatformOrderBy
      first: $brandOrderingPlatformLimit
      offset: $brandOrderingPlatformOffset
    ) {
      totalCount
      nodes {
        id
        orderingPlatform {
          id
          name
        }
        skuBrandOrderingPlatforms(filter: $filter) {
          totalCount
        }
        childBrandOrderingPlatformsList(
          filter: { skuBrandOrderingPlatforms: { some: $filter } }
          orderBy: $brandOrderingPlatformOrderBy
        ) {
          id
          orderingPlatform {
            id
            name
            parentId
          }
          ...CustomerProductPresenceCustomerColumn
        }
        ...CustomerProductPresenceCustomerColumn
      }
    }
    skus(
      filter: { skuBrandOrderingPlatforms: { some: $filter } }
      orderBy: $skuOrderBy
      first: $skuLimit
      offset: $skuOffset
    ) {
      totalCount
      nodes {
        id
        fullName
        ...CustomerPresenceTableSkuCell
      }
    }
  }
`);

const dataQuery = gql(/* GraphQL */ `
  query CustomerPresenceTableData($filter: SkuBrandOrderingPlatformFilter!) {
    skuBrandOrderingPlatformsList(filter: $filter) {
      id
      skuId
      brandOrderingPlatformId
      ...CustomerProductPresenceTableCell
    }
  }
`);

type SkuDatum = NonNullable<
  DocumentType<typeof setupQuery>["skus"]
>["nodes"][number];

type SkuBrandOrderingPlatformDatum = NonNullable<
  DocumentType<typeof dataQuery>["skuBrandOrderingPlatformsList"]
>[number];

type Datum = {
  sku: SkuDatum;
  skuBrandOrderingPlatformsByBrandOrderingPlatformId?: {
    [brandOrderingPlatformId: UUID]: SkuBrandOrderingPlatformDatum | undefined;
  };
};

const col = createColumnHelper<Datum>();

const BASE_COLUMNS = [
  col.accessor("sku.fullName", {
    id: "productName",
    header: "Product",
    cell: (cell) => (
      <CustomerPresenceTableSkuCell sku={cell.row.original.sku} />
    ),
    minSize: 125,
  }),
];

function getColumnForBrandOrderingPlatform(
  brandOrderingPlatform: FragmentType<typeof bopFragment>,
) {
  const bop = getFragmentData(bopFragment, brandOrderingPlatform);
  assertPresence(bop.orderingPlatform);
  return col.display({
    id: `brandOrderingPlatform-${bop.id}`,
    header: bop.orderingPlatform.name,
    cell: (cell) => {
      const sbop =
        cell.row.original.skuBrandOrderingPlatformsByBrandOrderingPlatformId?.[
          bop.id
        ];
      if (!sbop) return null;
      return (
        <CustomerProductPresenceTableCell skuBrandOrderingPlatform={sbop} />
      );
    },
    size: 250,
    meta: {
      props: { textAlign: "center", borderRightWidth: 1 },
      getCellProps: (cell) => {
        const sbop =
          cell.row.original
            .skuBrandOrderingPlatformsByBrandOrderingPlatformId?.[bop.id];
        return {
          backgroundColor: sbop ? "white" : "gray.50",
        };
      },
    },
  });
}

function getPageColumn(
  onPageChange: Dispatch<SetStateAction<number>>,
  {
    isPrevious = false,
    numPages,
  }: { isPrevious?: boolean; numPages?: number } = {},
) {
  return col.display({
    id: `${isPrevious ? "previous" : "next"}Page`,
    header: () => (
      <TooltipIconButton
        icon={
          <TriangleUpIcon transform={`rotate(${isPrevious ? "-" : ""}90deg)`} />
        }
        label={`${isPrevious ? "Previous" : "Next"} Page`}
        width="100%"
        display="block"
        borderRadius={0}
        colorScheme="softBlueButton"
        onClick={() =>
          onPageChange((prev) =>
            Math.min(
              Math.max(isPrevious ? prev - 1 : prev + 1, 0),
              numPages || Infinity,
            ),
          )
        }
      />
    ),
    size: 35,
    meta: {
      props: {
        padding: 0,
        backgroundColor: "softBlueButton.100",
        border: 0,
      },
      headerProps: {
        backgroundColor: "softBlueButton.500",
        sx: { ".sorting-enabled-header-wrapper": { width: "100%" } },
      },
    },
  });
}

function getDataQueryFilterFromSetupData(
  setupData: DocumentType<typeof setupQuery> | undefined,
) {
  if (
    !setupData ||
    !setupData.brandOrderingPlatforms?.nodes?.length ||
    !setupData.skus?.nodes?.length
  ) {
    return;
  }

  const bopIds = flatten(
    setupData.brandOrderingPlatforms.nodes.map((bop) =>
      bop.childBrandOrderingPlatformsList
        ? [
            bop.id,
            ...(bop.childBrandOrderingPlatformsList?.map((cbop) => cbop.id) ||
              []),
          ]
        : bop.id,
    ),
  );
  const skuIds = setupData.skus.nodes.map((sku) => sku.id);

  const filter: SkuBrandOrderingPlatformFilter = {
    skuId: { in: skuIds },
    brandOrderingPlatformId: { in: bopIds },
  };

  return filter;
}

const columnPinning: ColumnPinningState = { left: ["productName"] };

const orderByEnumsById: GQLOrderByEnumsById<SkusOrderBy> = {
  productName: [SkusOrderBy.FULL_NAME_ASC, SkusOrderBy.FULL_NAME_DESC],
};

export type Props = {
  filter?: SkuBrandOrderingPlatformFilter;
  isReady?: boolean;
};

export default function CustomerPresenceTable({
  filter: filterProp,
  isReady = true,
}: Props) {
  const brandId = useCurrentBrandId();

  const {
    pagination,
    onPaginationChange,
    limitOffset,
    getPageCount,
    setPageIndex,
  } = usePagination({ pageSize: 25 });

  const [columnPageIdx, setColumnPageIdx] = useState(0);

  const { sorting, onSortingChange, orderBy } = useGQLSorting(
    orderByEnumsById,
    [{ id: "productName", desc: false }],
  );

  const brandFilter = useMemo<SkuBrandOrderingPlatformFilter>(
    () => ({ brandOrderingPlatform: { brandId: { equalTo: brandId } } }),
    [brandId],
  );

  const sbopFilter = useCombineFilters(brandFilter, filterProp);

  const isPendingFilterResetRows = usePaginationReset(
    filterProp,
    setPageIndex,
    pagination.pageIndex,
  );

  const isPendingFilterResetColumns = usePaginationReset(
    filterProp,
    setColumnPageIdx,
    columnPageIdx,
  );

  const isPendingFilterReset =
    isPendingFilterResetRows || isPendingFilterResetColumns;

  const isPendingSortingReset = usePaginationReset(
    sorting,
    setPageIndex,
    pagination.pageIndex,
  );

  const {
    data: setupData,
    loading: isLoadingSetup,
    refetch: refetchSetup,
  } = useBrandQuery(setupQuery, {
    skip: !brandId || isPendingFilterReset || isPendingSortingReset || !isReady,
    variables: {
      filter: sbopFilter,
      brandOrderingPlatformLimit: COLUMN_PAGE_SIZE,
      brandOrderingPlatformOffset: columnPageIdx * COLUMN_PAGE_SIZE,
      skuOrderBy: orderBy,
      skuLimit: limitOffset.limit,
      skuOffset: limitOffset.offset,
    },
  });

  const setupResultsFilter = useMemo(
    () => getDataQueryFilterFromSetupData(setupData),
    [setupData],
  );

  const dataQueryFilter = useCombineFilters(sbopFilter, setupResultsFilter);

  const {
    data,
    loading: isLoadingData,
    refetch: refetchData,
  } = useBrandQuery(dataQuery, {
    skip: !setupResultsFilter,
    variables: { filter: dataQueryFilter },
  });

  const isLoading =
    isLoadingSetup ||
    isLoadingData ||
    typeof window === "undefined" ||
    !isReady;

  const { brandOrderingPlatforms: bopData, skus: skuData } = setupData || {};
  const { totalCount: numColumns } = bopData || {};
  const { totalCount } = skuData || {};

  const numColumnPages = Math.ceil((numColumns || 0) / COLUMN_PAGE_SIZE);
  const hasNextColumnPage = columnPageIdx < numColumnPages - 1;
  const hasPrevColumnPage = columnPageIdx > 0;

  useEffect(() => {
    if (
      (columnPageIdx > 0 && numColumnPages < columnPageIdx - 1) ||
      columnPageIdx < 0
    ) {
      setColumnPageIdx(0);
    }
  }, [numColumnPages, columnPageIdx]);

  const columns = useMemo(() => {
    if (!bopData?.nodes) return BASE_COLUMNS;

    const bopColumns = bopData.nodes.map((bop) => {
      assertPresence(bop.orderingPlatform);

      if (bop.childBrandOrderingPlatformsList?.length) {
        const childColumns = bop.childBrandOrderingPlatformsList.map(
          getColumnForBrandOrderingPlatform,
        );
        const subColumns = bop.skuBrandOrderingPlatforms.totalCount
          ? [getColumnForBrandOrderingPlatform(bop), ...childColumns]
          : childColumns;

        if (subColumns.length === 1) return subColumns[0];

        return col.group({
          id: `brandOrderingPlatform-parent-${bop.id}`,
          header: bop.orderingPlatform.name,
          columns: subColumns,
        });
      }

      return getColumnForBrandOrderingPlatform(bop);
    });

    return compact([
      ...BASE_COLUMNS,
      hasPrevColumnPage &&
        getPageColumn(setColumnPageIdx, {
          isPrevious: true,
          numPages: numColumnPages,
        }),
      ...bopColumns,
      hasNextColumnPage &&
        getPageColumn(setColumnPageIdx, { numPages: numColumnPages }),
    ]);
  }, [bopData, hasPrevColumnPage, hasNextColumnPage, numColumnPages]);

  const rowData = useMemo(() => {
    if (!skuData?.nodes) return;

    const sbopDataBySkuId = groupBy(
      data?.skuBrandOrderingPlatformsList,
      "skuId",
    );

    return skuData.nodes.map<Datum>((sku) => ({
      sku,
      skuBrandOrderingPlatformsByBrandOrderingPlatformId: keyBy(
        sbopDataBySkuId[sku.id],
        "brandOrderingPlatformId",
      ),
    }));
  }, [skuData?.nodes, data?.skuBrandOrderingPlatformsList]);

  const tableInstance = useReactTable({
    columns,
    data: usePrepApolloData(rowData),
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onPaginationChange,
    onSortingChange,
    manualPagination: true,
    manualSorting: true,
    enableSorting: true,
    state: { columnPinning, pagination, sorting },
    pageCount: getPageCount(totalCount),
  });

  const tableProps = useFullPageTableProps<Datum>([
    useFixedLayout(tableInstance),
    { tableProps: { variant: "condensed" } },
  ]);

  const handleRefresh = async () => {
    const oldSetupDataFilter = { ...setupResultsFilter };
    const result = await refetchSetup();
    const newSetupDataFilter = getDataQueryFilterFromSetupData(result.data);
    if (isEqual(oldSetupDataFilter, newSetupDataFilter)) {
      await refetchData();
    }
  };

  return (
    <FullPageTable
      {...{ isLoading, tableInstance, totalCount }}
      {...tableProps}
      noRowsPlaceholder={
        <Text>
          No products with distribution{" "}
          {filterProp ? "found" : "have been added to your account"}.
        </Text>
      }
      resultTypeLabel="products"
      onRefresh={handleRefresh}
    />
  );
}
